import React from "react";
import { Plus, Trash2, X, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useT } from "@/lib/LanguageContext";

/**
 * The quiz question editor shared by a course's quiz lesson (admin/LearnAdmin.jsx) and a
 * pathway's quiz step (admin/pathways/StepEditor.jsx). Both write the SAME question shape,
 * graded by one server engine (contract §8):
 *
 *   mcq:  { id?, question, question_type: "mcq", choices: string[2..10], correct_index }
 *   open: { id?, question, question_type: "open", expected_keywords: string[] }
 *
 * CONTROLLED. It owns no state: every edit calls `onChange(nextQuestions)` with a new
 * array, and the caller stores it.
 *
 * QUESTION IDS. `makeQuestionId` is called for each NEW question. When it is absent, a new
 * question carries no `id` and the server assigns one; that is the pathway contract (the
 * client never invents ids). The course editor keeps its historical client ids by passing
 * one.
 *
 * `scope` namespaces the test ids (`quiz-question-<scope>-<qIdx>`, ...). The course editor
 * passes the lesson index, the pathway editor the step key.
 *
 * @param {object}   props
 * @param {object[]} props.questions
 * @param {(next: object[]) => void} props.onChange
 * @param {string|number} props.scope
 * @param {() => string} [props.makeQuestionId]
 * @param {boolean}  [props.disabled]
 */
export default function QuizQuestionsEditor({ questions, onChange, scope, makeQuestionId, disabled = false }) {
  const t = useT("admin");
  const list = questions || [];

  const withId = (q) => (makeQuestionId ? { id: makeQuestionId(), ...q } : q);

  const addQuestion = (qtype) => {
    const base = { question: "", question_type: qtype };
    const q = qtype === "open"
      ? { ...base, expected_keywords: [] }
      : { ...base, choices: ["", ""], correct_index: 0 };
    onChange([...list, withId(q)]);
  };
  const updateQuestion = (qIdx, patch) => {
    const next = [...list];
    next[qIdx] = { ...next[qIdx], ...patch };
    onChange(next);
  };
  const removeQuestion = (qIdx) => onChange(list.filter((_, i) => i !== qIdx));
  const addChoice = (qIdx) => updateQuestion(qIdx, { choices: [...(list[qIdx].choices || []), ""] });
  const updateChoice = (qIdx, cIdx, value) => {
    const choices = [...list[qIdx].choices];
    choices[cIdx] = value;
    updateQuestion(qIdx, { choices });
  };
  const removeChoice = (qIdx, cIdx) => {
    const q = list[qIdx];
    if (q.choices.length <= 2) return;
    const choices = q.choices.filter((_, i) => i !== cIdx);
    let correct = q.correct_index;
    if (cIdx === correct) correct = 0;
    else if (cIdx < correct) correct -= 1;
    updateQuestion(qIdx, { choices, correct_index: correct });
  };

  return (
    <div className="space-y-3">
      {list.map((q, qIdx) => {
        const qtype = q.question_type || "mcq";
        return (
          <div key={q.id || qIdx} className="bg-white rounded-lg border border-amber-200 p-3" data-testid={`quiz-question-${scope}-${qIdx}`}>
            <div className="flex gap-2 mb-2 items-start">
              <span className={`text-[10px] px-2 py-1 rounded font-medium mt-1 inline-flex items-center gap-1 ${qtype === "open" ? "bg-violet-100 text-violet-900" : "bg-amber-100 text-amber-900"}`}>
                Q{qIdx + 1} · {qtype === "open" ? t("learn.qOpen") : t("learn.qMcq")}
              </span>
              <Input
                placeholder={t("learn.questionPlaceholder")}
                value={q.question}
                onChange={(e) => updateQuestion(qIdx, { question: e.target.value })}
                className="text-sm"
                disabled={disabled}
                data-testid={`question-text-${scope}-${qIdx}`}
              />
              <button
                type="button"
                onClick={() => removeQuestion(qIdx)}
                disabled={disabled}
                aria-label={t("learn.removeQuestion")}
                className="p-1.5 text-rose-500 hover:bg-rose-50 rounded disabled:opacity-30"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
            {qtype === "open" ? (
              <div className="ml-2 text-xs text-eh-muted italic">
                {t("learn.openAnswerDesc")}
              </div>
            ) : (
              <div className="space-y-1.5 ml-2">
                {(q.choices || []).map((choice, cIdx) => (
                  <div key={cIdx} className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => updateQuestion(qIdx, { correct_index: cIdx })}
                      disabled={disabled}
                      data-testid={`choice-correct-${scope}-${qIdx}-${cIdx}`}
                      aria-pressed={cIdx === q.correct_index}
                      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors flex-shrink-0 ${
                        cIdx === q.correct_index
                          ? "bg-emerald-500 border-emerald-500"
                          : "bg-white border-eh-border hover:border-emerald-400"
                      }`}
                      title={t("learn.markCorrect")}
                    >
                      {cIdx === q.correct_index && <Check className="w-3 h-3 text-white" />}
                    </button>
                    <Input
                      value={choice}
                      placeholder={`${t("learn.choiceWord")} ${cIdx + 1}`}
                      onChange={(e) => updateChoice(qIdx, cIdx, e.target.value)}
                      className="text-xs h-8"
                      disabled={disabled}
                      data-testid={`choice-text-${scope}-${qIdx}-${cIdx}`}
                    />
                    <button
                      type="button"
                      onClick={() => removeChoice(qIdx, cIdx)}
                      className="p-1 text-rose-400 hover:bg-rose-50 rounded disabled:opacity-30"
                      disabled={disabled || (q.choices || []).length <= 2}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => addChoice(qIdx)}
                  disabled={disabled}
                  className="text-xs text-amber-700 hover:text-amber-900 ml-7 mt-1 disabled:opacity-40"
                  data-testid={`add-choice-${scope}-${qIdx}`}
                >
                  {t("learn.addChoice")}
                </button>
              </div>
            )}
          </div>
        );
      })}

      <div className="flex gap-2 flex-wrap">
        <Button size="sm" variant="outline" onClick={() => addQuestion("mcq")} disabled={disabled} data-testid={`add-question-${scope}`} className="text-xs">
          <Plus className="w-3 h-3 mr-1" /> {t("learn.mcq")}
        </Button>
        <Button size="sm" variant="outline" onClick={() => addQuestion("open")} disabled={disabled} data-testid={`add-question-open-${scope}`} className="text-xs bg-violet-50 hover:bg-violet-100 border-violet-200">
          <Plus className="w-3 h-3 mr-1" /> {t("learn.longAnswer")}
        </Button>
      </div>
      {list.length === 0 && (
        <p className="text-xs text-amber-700 italic">{t("learn.addQuestionHint")}</p>
      )}
    </div>
  );
}
